import type {
  OrganizationProfileResponse as OrganizationProfile,
  OrganizationProfileUpdate,
} from '@/api/generated';
import { isFieldDirty, type DirtyFieldMap } from '@/lib/dirtyFields';
import {
  contactPart,
  contactToPayload,
  emailField,
  keepStoredAddress,
  optionalText,
  scopeNeedsStates,
  websiteField,
} from '@/lib/organization';
import {
  socialLinksSchema,
  toSocialLinkRows,
  toSocialLinksPayload,
} from '@/lib/socialLinks';
import { z } from 'zod';

/**
 * What a Super Admin may change about their own organization.
 *
 * The same rules as the staff form for the fields they share, taken from the
 * same place, minus everything a Super Admin may not touch: the web handle, the
 * status, and every commercial field. The backend keeps that list as
 * ORG_EDITABLE_FIELDS and refuses the rest, so a field that is not here is one
 * the page could not save anyway.
 *
 * The two images are not fields. They are uploaded on their own, and the
 * endpoint sets the record - so this form never carries a storage key.
 */

/** The four form fields behind each of the two contacts. */
const PRIMARY_CONTACT_FIELDS = [
  'contact_name',
  'contact_title',
  'contact_email',
  'contact_phone',
] as const;

const PUBLIC_CONTACT_FIELDS = [
  'public_contact_name',
  'public_contact_title',
  'public_contact_email',
  'public_contact_phone',
] as const;

export const profileFormSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(2, 'Enter the organization name')
      .max(200, 'Use 200 characters or fewer'),

    organization_type: z.string().optional(),
    description: optionalText(),
    website: websiteField(),

    geographic_scope: z.string().optional(),
    geographic_states: z.array(z.string()).default([]),
    scope_description: optionalText(),

    contact_name: optionalText(),
    contact_title: optionalText(),
    contact_email: emailField(),
    contact_phone: optionalText(),

    public_contact_name: optionalText(),
    public_contact_title: optionalText(),
    public_contact_email: emailField(),
    public_contact_phone: optionalText(),

    social_links: socialLinksSchema.default([]),
  })
  .refine(
    (values) =>
      !scopeNeedsStates(values.geographic_scope) || values.geographic_states.length > 0,
    {
      // A scope of "state" with no state named is not a scope at all.
      message: 'Choose at least one state for this scope',
      path: ['geographic_states'],
    },
  );

export type ProfileFormValues = z.input<typeof profileFormSchema>;
export type ProfileFormOutput = z.output<typeof profileFormSchema>;
export type ProfileDirtyFields = DirtyFieldMap<keyof ProfileFormValues>;

/**
 * Turn the stored organization into the flat shape the form holds.
 *
 * Every absent value becomes an empty string rather than staying null, because
 * a React input given null is an uncontrolled input, and the first keystroke in
 * it produces a warning and loses the form's record of what changed.
 */
export function toProfileFormValues(record: OrganizationProfile): ProfileFormValues {
  return {
    name: record.name,
    organization_type: record.organization_type ?? '',
    description: record.description ?? '',
    website: record.website ?? '',
    geographic_scope: record.geographic_scope ?? '',
    geographic_states: record.geographic_states ?? [],
    scope_description: record.scope_description ?? '',

    contact_name: contactPart(record.primary_contact, 'name'),
    contact_title: contactPart(record.primary_contact, 'title'),
    contact_email: contactPart(record.primary_contact, 'email'),
    contact_phone: contactPart(record.primary_contact, 'phone'),

    public_contact_name: contactPart(record.public_contact, 'name'),
    public_contact_title: contactPart(record.public_contact, 'title'),
    public_contact_email: contactPart(record.public_contact, 'email'),
    public_contact_phone: contactPart(record.public_contact, 'phone'),

    social_links: toSocialLinkRows(record.social_links),
  };
}

/** The two contacts as stored, whose addresses the update carries through. */
type StoredContacts = Pick<OrganizationProfile, 'primary_contact' | 'public_contact'>;

/**
 * Turn an edited form into a partial update, carrying only what changed.
 *
 * Sending the whole form on every save cannot tell "left alone" from "cleared",
 * and would overwrite whatever a colleague changed in the meantime with the
 * values this page happened to load with.
 *
 * Which fields changed cannot be read from the values alone: the schema turns a
 * blank optional field into undefined, so a field that was emptied looks exactly
 * like one that never held anything. That is what the dirty map is for.
 */
export function toProfileUpdatePayload(
  values: ProfileFormOutput,
  dirtyFields: ProfileDirtyFields,
  stored: StoredContacts,
): OrganizationProfileUpdate {
  const payload: OrganizationProfileUpdate = {};

  // The backend refuses to clear the name, and the form refuses a blank one
  // before anything is sent. The guard is kept because this function is the
  // last thing between the form and the request.
  if (isFieldDirty(dirtyFields, 'name') && values.name) {
    payload.name = values.name;
  }

  /*
   * Everything else that is text: what was typed, or null when the box was
   * emptied. Null is how the backend is told to clear a field, as against
   * leaving it alone by not mentioning it.
   */
  if (isFieldDirty(dirtyFields, 'organization_type')) {
    // Cast because the form holds a plain string: the select's options come
    // from the same list the backend enumerates, so the value is one of them or
    // it is empty.
    payload.organization_type =
      (values.organization_type as OrganizationProfileUpdate['organization_type']) ||
      null;
  }
  if (isFieldDirty(dirtyFields, 'description')) {
    payload.description = values.description || null;
  }
  if (isFieldDirty(dirtyFields, 'website')) {
    payload.website = values.website || null;
  }
  if (isFieldDirty(dirtyFields, 'geographic_scope')) {
    payload.geographic_scope =
      (values.geographic_scope as OrganizationProfileUpdate['geographic_scope']) || null;
  }
  if (isFieldDirty(dirtyFields, 'scope_description')) {
    payload.scope_description = values.scope_description || null;
  }

  // A list is replaced whole. There is no way to add one state without saying
  // what the rest of the list is.
  if (isFieldDirty(dirtyFields, 'geographic_states')) {
    payload.geographic_states = values.geographic_states;
  }

  /*
   * Each contact is stored as one thing, so it is replaced as one thing.
   * Editing only the phone number and sending only the phone number would drop
   * the name and the email address with it - and the stored address, which
   * the form does not show, goes back with it for the same reason.
   */
  if (PRIMARY_CONTACT_FIELDS.some((field) => isFieldDirty(dirtyFields, field))) {
    payload.primary_contact = keepStoredAddress(
      contactToPayload({
        name: values.contact_name,
        title: values.contact_title,
        email: values.contact_email,
        phone: values.contact_phone,
      }),
      stored.primary_contact,
    );
  }

  if (PUBLIC_CONTACT_FIELDS.some((field) => isFieldDirty(dirtyFields, field))) {
    payload.public_contact = keepStoredAddress(
      contactToPayload({
        name: values.public_contact_name,
        title: values.public_contact_title,
        email: values.public_contact_email,
        phone: values.public_contact_phone,
      }),
      stored.public_contact,
    );
  }

  // The links are one JSON value, so the whole set goes or none of it does.
  // Sending only the row that changed would leave a deleted link in place.
  if (isFieldDirty(dirtyFields, 'social_links')) {
    payload.social_links = toSocialLinksPayload(values.social_links);
  }

  return payload;
}
